import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import reviewsPricing from '@klv/shared/pricing/static/reviews.json';
import cdpPricing from '@klv/shared/pricing/static/cdp.json';
import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import Slider from '@klv/molecules/slider';
import Button from '@klv/atoms/button';
import { FeaturesList } from '../features-list';
import * as styles from './styles/platform-card.module.css';
import type { IAnalyticsItem, IPlatformCard } from './types';

interface ICdpPlan {
  profiles: number;
  total_monthly_cost: number;
}

interface IReviewsPlan {
  plan_price: number;
  monthly_orders: number;
}

type TCdpPlans = keyof typeof cdpPricing['cdp'];

type TReviewsPlans = keyof typeof reviewsPricing['reviews'];

const PlatformCard = ({
  description,
  featuresList,
  name,
  data,
  subText,
  subTextInfo,
  subTextInfoHeader,
  pricingDescription,
  activeProfiles,
  ...props
}: IPlatformCard) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;



  const findAnalyticsInfo = (analyticsArray: IAnalyticsItem[], inputValue: number) =>
    analyticsArray.find((item) => item.value === inputValue) || null;

  const result = useMemo(
    () =>
      name === 'Marketing Analytics' && activeProfiles !== undefined
        ? findAnalyticsInfo(data, activeProfiles)
        : null,
    [name, activeProfiles, data],
  );
  
  const [reviewsOrders, setReviewsOrders] = useState(250);
  const [cdpProfiles, setCdpProfiles] = useState(100000);
  const [price, setPrice] = useState<number | undefined>();

  const reviewsPlans = reviewsPricing.reviews;
  const cdpPlans = cdpPricing.cdp;

  const selectedCdpPlan: ICdpPlan = cdpPlans[cdpProfiles.toString() as TCdpPlans];
  const selectedReviewsPlan: IReviewsPlan = reviewsPlans[reviewsOrders.toString() as TReviewsPlans];

  const formattedPrice = price?.toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });

  const formattedPrice2 = result?.price?.toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });

  const handleClick = () => {
    gtmBasicEvent({
      category: 'platform card',
      action: 'demo cta',
      label: name.toLowerCase(),
    });
    return true;
  };

  useEffect(() => {
    if (name === 'Advanced Klaviyo Data Platform') {
      setPrice(selectedCdpPlan.total_monthly_cost);
    } else {
      setPrice(selectedReviewsPlan.plan_price);
    }
  }, [name, reviewsOrders, cdpProfiles, selectedCdpPlan, selectedReviewsPlan]);

  const handleSliderChange = (value: number) => {
    if (name === 'Advanced Klaviyo Data Platform') {
      setCdpProfiles(value);
    } else {
      setReviewsOrders(value);
    }
  };

  return (
    <div className={styles.platformCard} {...props}>
      <div className={styles.cardContents}>
        <div className={styles.planName}>{name}</div>
        <div className={styles.priceDescription}>{pricingDescription}</div>

        {name !== 'Marketing Analytics' ? (
          <>
            <div className={styles.priceWrapper}>
              <div className={styles.price}>{formattedPrice}</div>
              <div className={styles.timePeriod}>{t('organisms_pricing_per_month')}</div>
            </div>
            <div className={styles.sliderWrapper}>
              <Slider
                subText={subText}
                range={data}
                variant="platform"
                subTextInfoHeader={subTextInfoHeader}
                subTextInfo={subTextInfo}
                defaultValue={name === 'Customer Data Platform' ? cdpProfiles : reviewsOrders}
                onChange={handleSliderChange}
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.priceWrapper}>
              <div className={styles.price}>{formattedPrice2}</div>
              <div className={styles.timePeriod}>{t('organisms_pricing_per_month')}</div>
            </div>
            <div className={styles.priceInfo}>
              {result && result.value < 250000 ? (
                <div className={styles.activeProfiles}>
                  <div>{`${result?.label} ${t('organisms_pricing_contacts')}`}</div>
                  <div>{subText}</div>
                </div>
              ) : (
                <div className={styles.activeProfiles}>
                  {t('organisms_pricing_plans_paidCardContent_above250kContacts')}
                </div>
              )}
            </div>
          </>
        )}

        <div className={styles.divider} />
        <div className={styles.description}>{description}</div>
        <div className={styles.divider} />

        <div className={styles.buttonContainer}>
          <Button
            className={styles.button}
            text={t('organisms_navigation_dataActions_getADemo_button_label')}
            url={t('organisms_navigation_dataActions_getADemo_button_href')}
            buttonColor="dark"
            action={handleClick}
          />
        </div>

        <div className={styles.featuresListContainer}>
          {featuresList?.map((feature) => (
            <FeaturesList key={feature.listText} listIcon={feature.listIcon} listText={feature.listText} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlatformCard;

